import { usePageTitle } from "@/hooks/use-page-title";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { usePaymentPlans, useConditionWalletsDashboard, useAdminUsers, useSettings, usePlatformStats, QUERY_KEYS } from "@/hooks/use-queries";
import {
  LayoutDashboard,
  Users,
  Wallet,
  DollarSign,
  Sparkles,
  ShieldCheck,
  Settings,
  Building2,
  Activity,
  RefreshCw,
  Video,
  Megaphone,
  FlaskConical,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import type { Wallet as WalletType, CommunityChallenge, AchievementDefinition, ReferralCode } from "@shared/schema";
import { PageAbout } from "@/components/page-about";
import { OverviewDashboard } from "./admin-control/overview";
import { WalletsPlansDashboard } from "./admin-control/wallets-plans";
import { FinancialDashboard } from "./admin-control/financial";
import { EngagementDashboard } from "./admin-control/engagement";
import { ComplianceDashboard } from "./admin-control/compliance";
import { UsersDashboard } from "./admin-control/users";
import { ConfigDashboard } from "./admin-control/config";
import { EmployersDashboard } from "./admin-control/employers";
import { SuccessStoriesDashboard } from "./admin-control/success-stories";
import { MarketingStudioDashboard } from "./admin-control/marketing-studio";
import { InvestorLeadsDashboard } from "./admin-control/investor-leads";

export default function AdminControlPage() {
  usePageTitle("Admin Control Center", "Centralized dashboard for managing operations, finances, user engagement, compliance, and system configurations.");
  const [activeTab, setActiveTab] = useState("overview");

  const { data: users, isLoading: usersLoading } = useAdminUsers();

  const { data: wallets, isLoading: walletsLoading } = useQuery<WalletType[]>({
    queryKey: ["/api/admin/wallets"],
  });

  const { data: challenges } = useQuery<CommunityChallenge[]>({
    queryKey: ["/api/challenges"],
  });

  const { data: achievements } = useQuery<AchievementDefinition[]>({
    queryKey: ["/api/achievements"],
  });

  const { data: referralCodes } = useQuery<ReferralCode[]>({
    queryKey: ["/api/admin/referral-codes"],
  });

  const { data: ambassadors } = useQuery<any[]>({
    queryKey: ["/api/ambassadors"],
  });

  const { data: auditLogs } = useQuery<any[]>({
    queryKey: ["/api/audit-logs"],
  });

  const { data: settings } = useSettings();

  const { data: subscriptions } = useQuery<any[]>({
    queryKey: ["/api/admin/subscriptions"],
  });

  const { data: paymentPlans } = usePaymentPlans();

  const { data: hospitalPartners } = useQuery<any[]>({
    queryKey: ["/api/admin/hospital-partners"],
  });

  const { data: conditionWalletsDashboard } = useConditionWalletsDashboard();
  const { data: platformStats } = usePlatformStats();
  const { data: appMode } = useQuery<{ isDemoEnv: boolean; appEnv: string }>({
    queryKey: ['/api/app-mode'],
    staleTime: Infinity,
  });

  const isLoading = usersLoading || walletsLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-64" />
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="page-admin-control">
      <PageAbout
        title="Admin Control Center"
        description="Centralized administrative dashboard for managing all platform operations. Monitor wallets, payment schedules, partnerships, and community engagement across the HealthWallet platform."
        features={[
          "Real-time overview of wallets, partnerships, and users",
          "Manage payment schedules, hardship requests, and contributions",
          "Configure challenges, achievements, and referral programs",
          "Monitor compliance, audit logs, and data access",
          "Access advanced configuration and system settings"
        ]}
        tips={[
          "Check the Wallets & Plans tab for pending hardship requests",
          "Review Compliance tab for any alerts requiring attention",
          "Use Configuration to adjust system-wide settings"
        ]}
      />
      
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold" data-testid="text-page-title">Admin Control Center</h1>
          <p className="text-muted-foreground mt-1">
            Centralized management dashboard for all platform features
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {appMode?.isDemoEnv && (
            <Badge variant="secondary" className="gap-1 border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400" data-testid="badge-demo-env">
              <FlaskConical className="w-3 h-3" />
              Demo Environment
            </Badge>
          )}
          <Badge variant="outline" className="gap-1">
            <Activity className="w-3 h-3" />
            System Online
          </Badge>
          <Button variant="outline" size="default" className="min-h-[44px]" onClick={() => queryClient.invalidateQueries()} data-testid="button-refresh-data">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Data
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto no-scrollbar w-full" data-testid="admin-tabs-scroll">
        <TabsList className="inline-flex min-w-full h-auto min-h-[44px]">
          <TabsTrigger value="overview" className="shrink-0 flex-col sm:flex-row gap-1 py-2 px-3 text-xs sm:text-sm min-h-[44px]" data-testid="tab-overview">
            <LayoutDashboard className="w-4 h-4" />
            <span className="hidden sm:inline">Overview</span>
            <span className="sm:hidden text-[10px] leading-none">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="wallets-plans" className="shrink-0 flex-col sm:flex-row gap-1 py-2 px-3 text-xs sm:text-sm min-h-[44px]" data-testid="tab-wallets-plans">
            <Wallet className="w-4 h-4" />
            <span className="hidden sm:inline">Wallets & Plans</span>
            <span className="sm:hidden text-[10px] leading-none">Wallets</span>
          </TabsTrigger>
          <TabsTrigger value="financial" className="shrink-0 flex-col sm:flex-row gap-1 py-2 px-3 text-xs sm:text-sm min-h-[44px]" data-testid="tab-financial">
            <DollarSign className="w-4 h-4" />
            <span className="hidden sm:inline">Financial</span>
            <span className="sm:hidden text-[10px] leading-none">Finance</span>
          </TabsTrigger>
          <TabsTrigger value="engagement" className="shrink-0 flex-col sm:flex-row gap-1 py-2 px-3 text-xs sm:text-sm min-h-[44px]" data-testid="tab-engagement">
            <Sparkles className="w-4 h-4" />
            <span className="hidden sm:inline">Engagement</span>
            <span className="sm:hidden text-[10px] leading-none">Engage</span>
          </TabsTrigger>
          <TabsTrigger value="compliance" className="shrink-0 flex-col sm:flex-row gap-1 py-2 px-3 text-xs sm:text-sm min-h-[44px]" data-testid="tab-compliance">
            <ShieldCheck className="w-4 h-4" />
            <span className="hidden sm:inline">Compliance</span>
            <span className="sm:hidden text-[10px] leading-none">Comply</span>
          </TabsTrigger>
          <TabsTrigger value="users" className="shrink-0 flex-col sm:flex-row gap-1 py-2 px-3 text-xs sm:text-sm min-h-[44px]" data-testid="tab-users">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Users</span>
            <span className="sm:hidden text-[10px] leading-none">Users</span>
          </TabsTrigger>
          <TabsTrigger value="config" className="shrink-0 flex-col sm:flex-row gap-1 py-2 px-3 text-xs sm:text-sm min-h-[44px]" data-testid="tab-config">
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Config</span>
            <span className="sm:hidden text-[10px] leading-none">Config</span>
          </TabsTrigger>
          <TabsTrigger value="employers" className="shrink-0 flex-col sm:flex-row gap-1 py-2 px-3 text-xs sm:text-sm min-h-[44px]" data-testid="tab-employers">
            <Building2 className="w-4 h-4" />
            <span className="hidden sm:inline">Employers</span>
            <span className="sm:hidden text-[10px] leading-none">Employ</span>
          </TabsTrigger>
          <TabsTrigger value="marketing" className="shrink-0 flex-col sm:flex-row gap-1 py-2 px-3 text-xs sm:text-sm min-h-[44px]" data-testid="tab-marketing">
            <Video className="w-4 h-4" />
            <span className="hidden sm:inline">Marketing</span>
            <span className="sm:hidden text-[10px] leading-none">Market</span>
          </TabsTrigger>
          <TabsTrigger value="investor-leads" className="shrink-0 flex-col sm:flex-row gap-1 py-2 px-3 text-xs sm:text-sm min-h-[44px]" data-testid="tab-investor-leads">
            <Megaphone className="w-4 h-4" />
            <span className="hidden sm:inline">Investor Leads</span>
            <span className="sm:hidden text-[10px] leading-none">Invest</span>
          </TabsTrigger>
        </TabsList>
        </div>

        <TabsContent value="overview" className="space-y-6">
          <OverviewDashboard 
            users={users || []}
            wallets={wallets || []}
            challenges={challenges || []}
            referralCodes={referralCodes || []}
            auditLogs={auditLogs || []}
            hospitalPartners={hospitalPartners || []}
            conditionWalletsDashboard={conditionWalletsDashboard}
            platformStats={platformStats}
          />
        </TabsContent>

        <TabsContent value="wallets-plans" className="space-y-6">
          <WalletsPlansDashboard 
            wallets={wallets || []}
            paymentPlans={paymentPlans || []}
          />
        </TabsContent>

        <TabsContent value="financial" className="space-y-6">
          <FinancialDashboard 
            wallets={wallets || []}
            subscriptions={subscriptions || []}
          />
        </TabsContent>

        <TabsContent value="engagement" className="space-y-6">
          <EngagementDashboard 
            challenges={challenges || []}
            achievements={achievements || []}
            referralCodes={referralCodes || []}
            ambassadors={ambassadors || []}
          />
          <SuccessStoriesDashboard />
        </TabsContent>

        <TabsContent value="compliance" className="space-y-6">
          <ComplianceDashboard 
            auditLogs={auditLogs || []}
            users={users || []}
          />
        </TabsContent>

        <TabsContent value="users" className="space-y-6">
          <UsersDashboard users={users || []} />
        </TabsContent>

        <TabsContent value="config" className="space-y-6">
          <ConfigDashboard settings={settings} />
        </TabsContent>

        <TabsContent value="employers" className="space-y-6">
          <EmployersDashboard />
        </TabsContent>

        <TabsContent value="marketing" className="space-y-6">
          <MarketingStudioDashboard />
        </TabsContent>

        <TabsContent value="investor-leads" className="space-y-6">
          <InvestorLeadsDashboard />
        </TabsContent>
      </Tabs>
    </div>
  );
}
